import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-hot-toast";
import MoonLoader from "react-spinners/MoonLoader";
import CustomFeedDrawer from "./CustomFeedDrawer";
import SinglePropertyFeed from "../SinglePropertyFeed/SinglePropertyFeed";
import { getListingFeedProperties } from "../../../../../services/PropertyService";
import { getUserFeeds } from "../../../../../services/UserFeedService";
import { useUser } from "../../../../../context/UserContext";
import CustomSvg from "../../../../../components/common/custom-svg/custom-svg";
import addFirstFeedIcon from "../../../../../assets/icons-v2/add_first_feed.png";
import useCustomLoginWithRedirect from "../../../../../hooks/useCustomLoginWithRedirect";
import { THIRTY_MINUTES } from "../../../../../constants/time";

const INITIAL_LIMIT = 10;
const CACHE_TIME = THIRTY_MINUTES; // 30 minutes

/**
 * CustomFeed component displays a list of properties that the user is following
 * @param {Object} props - Component props
 * @param {boolean} props.show - Controls the visibility of the CustomFeedDrawer
 * @param {Function} props.onHide - Callback function to hide the CustomFeedDrawer
 * @returns {JSX.Element} CustomFeed component
 */
const CustomFeed = ({ show, onHide }) => {
    const { userState } = useUser();
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const { isAuthenticated } = useAuth0();
    const loaderRef = useRef(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const {
        data: feedsResponse = { data: [] },
        isLoading: feedsLoading,
        error: feedsError,
    } = useQuery({
        queryKey: ["userFeeds", userState?.id],
        queryFn: () => getUserFeeds(userState?.id),
        enabled: !!userState?.id,
        refetchOnWindowFocus: true,
        staleTime: 0,
    });
    const feeds = feedsResponse?.data || [];
    const hasFeeds = Array.isArray(feeds) && feeds.length > 0;

    // Only enable property query if feeds exist
    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: ["live-variant-customFeed-properties", userState?.id],
        queryFn: ({ pageParam = 1 }) =>
            getListingFeedProperties({
                page: pageParam,
                limit: INITIAL_LIMIT,
                userId: userState?.id ?? null,
                userFeedPreferenceId: "", // Empty for now to fetch all user feed preference
                feedType: "following",
            }),
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage?.data?.hasNextPage) {
                return allPages.length + 1;
            }
            return undefined;
        },
        staleTime: 0,
        cacheTime: CACHE_TIME,
        enabled: hasFeeds, // Only run if feeds exist
        onError: (error) => {
            toast.error(error?.response?.data?.message || error?.message || "An error occurred.");
        },
    });

    const handleAddFeedClick = () => {
        if (!isAuthenticated) {
            customLoginWithRedirect({
                authorizationParams: {
                    redirect_uri: window.location.origin + "/social",
                },
                appState: {
                    toggleProfile: true,
                    version: "social",
                    event: {
                        name: "tutorial_register_click",
                        referral_code: localStorage.getItem("referral_code"),
                    },
                },
            });
            return;
        } else {
            // If authenticated, open the drawer directly
            setDrawerOpen(true);
        }
    };

    useEffect(() => {
        if (!hasFeeds) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 1.0 }
        );

        const currentLoader = loaderRef.current;
        if (currentLoader) observer.observe(currentLoader);
        return () => {
            if (currentLoader) observer.unobserve(currentLoader);
        };
    }, [hasFeeds, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const allProperties = data?.pages.flatMap((page) => page.data?.data) || [];

    if (isAuthenticated) {
        // Show loading for feeds
        if (feedsLoading) {
            return (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-3/4">
                    <MoonLoader size={40} color="#63c19f" />
                </div>
            );
        }

        // Show error for feeds
        if (feedsError) {
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-3/4 tw-text-center tw-text-gray-500 tw-p-8">
                    <div className="tw-mb-4 tw-text-red-500">
                        {feedsError?.response?.data?.message ||
                            feedsError?.message ||
                            "Failed to load feeds."}
                    </div>
                </div>
            );
        }
    }

    // If no feeds, show Add your first Feed screen
    if (!hasFeeds || !isAuthenticated) {
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-pt-16">
                <CustomSvg src={addFirstFeedIcon} size={{ width: 90, height: 90 }} className="tw-mb-6" />
                <div className="tw-font-bold tw-text-xl tw-mb-2">Add your first Feed</div>
                <div className="tw-text-gray-500 tw-mb-6 tw-text-center" style={{ maxWidth: 320 }}>
                    Keep tabs on homes near you, plan your next move, or find the home from your fantasies
                </div>
                <button
                    className="tw-bg-orange-400 tw-text-white tw-rounded-lg tw-px-8 tw-py-3 tw-font-semibold tw-text-base hover:tw-bg-orange-500 focus:tw-outline-none"
                    onClick={() => handleAddFeedClick()}
                >
                    Add a feed
                </button>
                {drawerOpen && (
                    <CustomFeedDrawer show={drawerOpen} onHide={() => setDrawerOpen(false)} feeds={feeds} />
                )}
            </div>
        );
    }

    // If feeds exist, show the property feed list
    return (
        <div className="tw-flex tw-flex-col tw-w-full">
            {/* Show message if there are feeds but no properties fetched */}
            {hasFeeds && !isLoading && allProperties.length === 0 && (
                <div className="tw-text-center tw-text-gray-500 tw-my-8">
                    No properties found for your current feed filters. Try adjusting your custom feed
                    criteria.
                </div>
            )}
            {isLoading && allProperties.length === 0 ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-3/4">
                    <MoonLoader size={40} color="#63c19f" />
                </div>
            ) : (
                <>
                    {allProperties.map((property) => (
                        <SinglePropertyFeed 
                            key={property.id} 
                            property={property} 
                            feedType="following"
                        />
                    ))}

                    {hasNextPage && (
                        <div ref={loaderRef} className="tw-flex tw-items-center tw-justify-center tw-py-4">
                            <MoonLoader size={30} color="#63c19f" />
                        </div>
                    )}
                </>
            )}
            {/* Show the drawer if requested */}
            {(show || drawerOpen) && (
                <CustomFeedDrawer
                    show={show || drawerOpen}
                    onHide={() => {
                        setDrawerOpen(false);
                        if (show && onHide) {
                            onHide();
                        }
                    }}
                    feeds={feeds}
                />
            )}
        </div>
    );
};

CustomFeed.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
};

export default CustomFeed;
